/**
 * Helper untuk penggunaan i18n yang konsisten antara development dan production
 */

import { getI18n } from "./index";

/**
 * Mengubah locale dengan cara yang bekerja baik di legacy maupun composition mode
 * @param {string} locale - Locale yang akan diatur ('en', 'id', dll)
 * @param {boolean} reload - Paksa reload halaman setelah mengganti locale
 * @param {boolean} useUrlParam - Gunakan parameter URL untuk reload
 * @returns {boolean} Berhasil atau tidak
 */
export function setLocale(locale, reload = true, useUrlParam = true) {
  // Validasi locale
  if (locale !== "en" && locale !== "id") {
    console.error(`Locale tidak valid: ${locale}`);
    return false;
  }

  // Pastikan semua metode penyimpanan diperbarui
  try {
    // 1. Simpan di localStorage untuk persistensi
    localStorage.setItem("locale", locale);

    // 2. Perbarui instance i18n jika sudah tersedia
    const i18n = getI18n();
    if (i18n) {
      // Gunakan blok try-catch terpisah agar kesalahan di sini tidak menggagalkan seluruh proses
      try {
        if (i18n.mode === "legacy") {
          // Legacy mode (Vue 2 style API)
          i18n.global.locale = locale;
        } else {
          // Composition API (Vue 3 style)
          i18n.global.locale.value = locale;
        }
        console.log(`i18n instance berhasil diperbarui ke: ${locale}`);
      } catch (i18nError) {
        console.error(`Error saat memperbarui i18n instance:`, i18nError);
        // Lanjutkan proses meskipun ada error di sini
      }
    } else {
      console.warn("i18n belum diinisialisasi, hanya mengubah localStorage");
    }

    // 3. Expose ke window agar dapat diakses oleh kode lain
    if (window.i18n) {
      try {
        if (window.i18n.mode === "legacy") {
          window.i18n.global.locale = locale;
        } else {
          window.i18n.global.locale.value = locale;
        }
        console.log(`window.i18n berhasil diperbarui ke: ${locale}`);
      } catch (windowError) {
        console.error(`Error saat memperbarui window.i18n:`, windowError);
      }
    }

    // Log selalu (baik di development maupun production) untuk debug
    console.log(
      `Locale diatur ke: ${locale} (reload: ${reload}, useUrlParam: ${useUrlParam})`
    );

    // 4. Reload halaman jika diminta (untuk menerapkan perubahan secara paksa)
    if (reload) {
      // Beri sedikit delay untuk memastikan localStorage telah disimpan
      setTimeout(() => {
        if (useUrlParam) {
          // Gunakan parameter URL untuk memastikan locale baru digunakan saat reload
          const url = new URL(window.location);
          url.searchParams.set("locale", locale);
          url.searchParams.set("_", new Date().getTime()); // Cache buster
          window.location.href = url.toString();
        } else {
          // Reload sederhana tanpa parameter URL
          window.location.reload();
        }
      }, 100);
    }

    // Broadcast event bahwa locale telah berubah
    window.dispatchEvent(
      new CustomEvent("locale:changed", { detail: { locale } })
    );

    return true;
  } catch (error) {
    console.error(`Error saat mengubah locale ke ${locale}:`, error);
    return false;
  }
}

/**
 * Mendapatkan locale saat ini dengan cara yang bekerja baik di legacy maupun composition mode
 * @returns {string} Current locale
 */
export function getLocale() {
  const i18n = getI18n();
  if (!i18n) {
    return localStorage.getItem("locale") || "id";
  }

  if (i18n.mode === "legacy") {
    // Legacy mode
    return i18n.global.locale;
  } else {
    // Composition API
    return i18n.global.locale.value;
  }
}

/**
 * Wrapper untuk translate yang bekerja baik di legacy maupun composition mode
 * @param {string} key - Key terjemahan
 * @param {Object} params - Parameter untuk terjemahan
 * @returns {string} String terjemahan
 */
export function t(key, params = {}) {
  const i18n = getI18n();
  if (!i18n) {
    return key;
  }

  return i18n.global.t(key, params);
}

/**
 * Wrapper untuk translate dengan fallback jika key sama dengan hasil terjemahan
 * @param {string} key - Key terjemahan
 * @param {string} fallback - Teks fallback jika terjemahan tidak ditemukan
 * @param {Object} params - Parameter untuk terjemahan
 * @returns {string} String terjemahan atau fallback
 */
export function tWithFallback(key, fallback, params = {}) {
  const translation = t(key, params);
  return translation === key ? fallback : translation;
}

/**
 * Fungsi darurat untuk mengganti bahasa di mode production
 * @param {string} locale - Locale yang akan diatur
 * @returns {boolean} Berhasil atau tidak
 */
export function emergencyLanguageSwitch(locale) {
  console.warn("Menggunakan emergency language switch untuk: " + locale);

  try {
    // 1. Simpan di localStorage
    localStorage.setItem("locale", locale);

    // 2. Perbarui URL (tanpa reload)
    const url = new URL(window.location);
    url.searchParams.set("locale", locale);
    window.history.replaceState({}, "", url.toString());

    // 3. Gunakan setiap metode yang mungkin untuk mengganti i18n instance
    if (window.i18n) {
      try {
        // Legacy mode
        if (window.i18n.mode === "legacy") {
          window.i18n.global.locale = locale;
        }
        // Composition mode
        else if (
          window.i18n.global &&
          window.i18n.global.locale &&
          typeof window.i18n.global.locale === "object"
        ) {
          window.i18n.global.locale.value = locale;
        }

        console.log("Berhasil mengubah window.i18n locale ke: " + locale);

        // Broadcast event
        window.dispatchEvent(
          new CustomEvent("locale:changed", { detail: { locale } })
        );

        // 4. Force reload setelah delay
        setTimeout(() => {
          window.location.reload();
        }, 100);

        return true;
      } catch (err) {
        console.error("Gagal mengubah i18n: ", err);
      }
    }

    // Fallback: hard reload dengan parameter locale
    const reloadUrl = new URL(window.location);
    reloadUrl.searchParams.set("locale", locale);
    reloadUrl.searchParams.set("_", Date.now());
    window.location.href = reloadUrl.toString();

    return true;
  } catch (error) {
    console.error("Emergency language switch gagal: ", error);
    return false;
  }
}
